import { Patient } from "../components/order-components/Patient";
import { PartialDentureInfo } from "../components/order-components/pap-workflow-components/PartialDentureInfo";
import { HooksAndOptions } from "../components/order-components/pap-workflow-components/HooksAndOptions";
import { MaterialAndShadeSelection } from "../components/order-components/pap-workflow-components/MaterialAndShadeSelection";
import { PartialDentureWorkflow } from "../components/order-components/pap-workflow-components/PartialDentureWorkflow";
import { Order, Product, User } from "../types/interfaces";
import OrderRecap from "../components/recap/orderRecap";

export type WorkflowType = {
  label: string;
  component: React.ReactNode;
  onNext: () => Promise<void>;
};

export enum HookPlacementBy {
  JASPER = "JASPER",
  DOCTOR = "DOCTOR",
}

export const workflowDefaultValues = {
  PARTIAL_DENTURE: {
    defaultValues: (order?: Order) => {
      return {
        firstName: order?.patient?.firstName,
        lastName: order?.patient?.lastName,
        birthDate: order?.patient?.birthdate,
        doctorId: order?.user?.id,
        sex: order?.patient?.sex,
        isReplacement: false,
        teeths: {
          missingTeeth: [],
          toExtract: [],
        },
        material: "",
        brand: "",
        shade: "",
        gingivaShade: "",
        hookChoice: "",
        hooksPosition: [],
        hooksMaterial: "",
        workflow: "",
        comment: "",
        options: [],
      };
    },
  },
};

export const workflowFields = {
  PARTIAL_DENTURE: [
    {
      firstName: "",
      lastName: "",
      birthDate: "",
      doctorId: "",
      sex: "",
    },
    {
      isReplacement: "",
      teeths: {
        missingTeeth: [],
        toExtract: [],
      },
    },
    {
      material: "",
      brand: "",
      shade: "",
      gingivaShade: "",
    },
    {
      hookChoice: "",
      hooksPosition: [],
      hooksMaterial: "",
      options: [],
    },
    {
      workflow: "",
    },
    {
      comment: "",
    },
  ],
};

export const stepLabels = {
  PARTIAL_DENTURE: {
    PATIENT: "Patient",
    INFORMATION: "Informations",
    PROTHESIS: "Prothèse",
    HOOKS_AND_OPTIONS: "Crochets et options",
    FLOW: "Flux de travail",
    RECAP: "Récap",
  },
};

export const WORKFLOW_MAP = {
  PARTIAL_DENTURE: {
    steps: (
      order?: Order,
      user?: User,
      removableItemChoicesData?: any,
      nextStep?: () => Promise<void>,
      product?: Product
    ) => [
      {
        label: stepLabels.PARTIAL_DENTURE.PATIENT,
        component: (
          <Patient
            order={order}
            user={user}
          />
        ),
        onNext: () => nextStep(),
      },
      {
        label: stepLabels.PARTIAL_DENTURE.INFORMATION,
        component: <PartialDentureInfo product={product} />,
        onNext: () => nextStep(),
      },
      {
        label: stepLabels.PARTIAL_DENTURE.PROTHESIS,
        component: (
          <MaterialAndShadeSelection
            removableItemChoicesData={removableItemChoicesData}
            product={product}
          />
        ),
        onNext: () => nextStep(),
      },
      {
        label: stepLabels.PARTIAL_DENTURE.HOOKS_AND_OPTIONS,
        component: <HooksAndOptions product={product} />,
        onNext: () => nextStep(),
      },
      {
        label: stepLabels.PARTIAL_DENTURE.FLOW,
        component: <PartialDentureWorkflow product={product} />,
        onNext: () => nextStep(),
      },
      {
        label: stepLabels.PARTIAL_DENTURE.RECAP,
        component: (
          <OrderRecap
            order={
              order as Order & {
                address: {
                  streetNumber: string;
                  streetName: string;
                  city: string;
                  postcode: string;
                };
              } & User & {
                  patient: {
                    firstName: string;
                    lastName: string;
                    birthdate: string;
                    sex: string;
                  };
                }
            }
            refetchOrder={() => {}}
            getDeliveryDate={() =>
              Promise.resolve({
                data: {
                  getOrderEstimatedDeliveryDate: {
                    deliveryDate: new Date(),
                    minimalRushDeliveryDate: new Date(),
                    bankHolidays: [],
                  },
                },
              })
            }
            user={
              user as User & {
                accessToken: string;
                customDeliveryDateEnabled: boolean;
              }
            }
          />
        ),
      },
    ],
  },
};

export const useWorkflow = (
  workflow: string[],
  order?: Order,
  user?: User,
  removableItemChoicesData?: any,
  nextStep?: () => Promise<void>,
  product?: Product
): WorkflowType[] => {
  return workflow
    .map(step => {
      return WORKFLOW_MAP[step]?.steps(
        order,
        user,
        removableItemChoicesData,
        nextStep,
        product
      );
    })
    .flat();
};

export const useWorkflowFieldsAndDefaultValues = (
  workflow: string[],
  order?: Order
) => {
  const currentWorkflowFields = workflow.map(step => {
    return workflowFields[step];
  });

  const currentWorkflowDefaultValues = workflow.map(step => {
    return workflowDefaultValues[step].defaultValues(order);
  });

  return {
    workflowFields: currentWorkflowFields,
    workflowDefaultValues: currentWorkflowDefaultValues,
  };
};

export const mapFieldsToValues = (fields: string[], values: string[]) => {
  return fields
    .map((field: string, index: number) => {
      return {
        [field]: values[index],
      };
    })
    .reduce((acc, curr) => {
      return { ...acc, ...curr };
    }, {});
};
